import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';

const ResourceLibrary = ({ trainerId }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    if (trainerId) {
      const fetchResources = async () => {
        try {
          const { data, error } = await supabase
            .from('trainer_resources_20250219')
            .select('*')
            .eq('trainer_id', trainerId)
            .eq('is_published', true)
            .order('category', { ascending: true })
            .order('title', { ascending: true });

          if (error) throw error;
          setResources(data || []);
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchResources();
    }
  }, [trainerId]);

  const filteredResources = resources.filter(r => 
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    r.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = [...new Set(resources.map(r => r.category))];

  return (
    <div className="space-y-8">
      <div className="bg-indigo-600 rounded-[2.5rem] p-8 text-white shadow-xl shadow-indigo-100 relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-3xl font-black mb-2">Knowledge Library</h2>
          <p className="text-indigo-100 font-medium">Everything you need to succeed with your training journey.</p>
        </div>
        <SafeIcon name="Book" className="absolute -right-8 -bottom-8 w-40 h-40 text-white/10 rotate-12" />
      </div>

      <div className="relative">
        <SafeIcon name="Search" className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input 
          type="text" 
          placeholder="Search FAQs, guides, and support docs..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-14 pr-6 py-5 bg-white border-2 border-gray-100 rounded-[2rem] font-bold text-gray-700 shadow-sm outline-none focus:border-indigo-500 transition-all"
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse">
          {[1,2,3,4].map(i => <div key={i} className="h-40 bg-white rounded-[2rem] border border-gray-100" />)}
        </div>
      ) : resources.length === 0 ? (
        <div className="py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
            <SafeIcon name="Info" className="w-8 h-8" />
          </div>
          <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">No resources available yet</p>
        </div>
      ) : (
        <div className="space-y-12">
          {categories.map(cat => (
            <div key={cat} className="space-y-4">
              <h3 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] ml-1">{cat}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredResources.filter(r => r.category === cat).map(resource => (
                  <button 
                    key={resource.id} 
                    onClick={() => setSelectedResource(resource)}
                    className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:border-indigo-600 hover:shadow-md transition-all text-left group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-black text-gray-900 group-hover:text-indigo-600 transition-colors">{resource.title}</h4>
                      <SafeIcon name={resource.external_url ? "ExternalLink" : "ArrowRight"} className="w-4 h-4 text-gray-300 group-hover:text-indigo-600 transition-colors" />
                    </div>
                    <p className="text-xs text-gray-400 font-medium line-clamp-2">
                      {resource.content ? resource.content.replace(/<[^>]*>/g, '') : 'Click to view details...'}
                    </p>
                  </button>
                ) )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {selectedResource && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[10005]" onClick={() => setSelectedResource(null)}>
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 border-b bg-gray-50/50 flex justify-between items-start">
                <div>
                  <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black uppercase px-2 py-0.5 rounded-md mb-2 inline-block tracking-widest">{selectedResource.category}</span>
                  <h3 className="text-2xl font-black text-gray-900 leading-tight">{selectedResource.title}</h3>
                </div>
                <button onClick={() => setSelectedResource(null)} className="p-3 hover:bg-gray-200 rounded-2xl transition-colors">
                  <SafeIcon name="X" className="w-6 h-6 text-gray-400" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-8 prose prose-indigo max-w-none text-gray-700 leading-relaxed">
                <div dangerouslySetInnerHTML={{ __html: selectedResource.content }} />
                
                {selectedResource.external_url && (
                  <div className="mt-10 pt-8 border-t border-gray-100">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Official Documentation</p>
                    <a 
                      href={selectedResource.external_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black shadow-lg hover:bg-indigo-700 transition-all no-underline"
                    >
                      <SafeIcon name="ExternalLink" /> View External Source
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResourceLibrary;